import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { MessageMiddleware, MiddlewareContextFactory } from "../../middlewares";
import { ChatDetail, ChatMsgDetail } from "../../store/ChatAction";
import { ChatAgents } from "../../store/ChatState";
import { ChatMessageRequest } from "../../xapp/ChatServerMessage";
import { ChatChipsContainer } from "../ChatChips";
import { ChatMessage } from "../ChatMessage";
import { ChatRatingContainer } from "../ChatRating";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { FailureMessage } from "../FailureMessage";
import { OfflineFormContainer } from "../OfflineForm";
import PrechatFormContainer from "../PrechatForm/PrechatFormContainer";
import { QueuePosition } from "../QueuePosition";
import { SystemMessage } from "../SystemMessage";
import { TypingStatus } from "../TypingStatus";
import "./MessageList.scss";
import { log } from "../../utils";
import { OpenUrlCallback } from "../../hooks/useOpenUrlCallback";
import { ROUTER_USER } from "../../xapp/StentorRouterChat";

export interface Props {
  visible: boolean;
  messages: readonly ChatDetail[];
  /**
   * List of possible agents
   */
  readonly agents: ChatAgents;
  isOffline: boolean;
  isChatting: boolean;
  queuePosition: number;
  lastRatingRequestTimestamp: number;
  hasRating: boolean;
  visitorId: string;
  readonly messageMiddleware?: MessageMiddleware;
  readonly textTypingStatusEnabled?: boolean;
  /**
   * Default agent
   */
  readonly agent?: ChatUserInfo;
  onSend(msg: ChatMessageRequest): void;
  onWrite(msg: ChatMessageRequest): void;
  onOpenUrl: OpenUrlCallback;
}

const defaultProps: Props = {
  visible: true,
  messages: [],
  agents: {},
  isOffline: true,
  isChatting: false,
  queuePosition: 0,
  lastRatingRequestTimestamp: 0,
  hasRating: false,
  visitorId: "",
  onSend: () => Promise.resolve(),
  onWrite: () => Promise.resolve(),
  onOpenUrl: () => { }
};

const MessageList: FC<React.PropsWithChildren<Props>> = (props = defaultProps) => {
  const { onSend, onWrite, onOpenUrl } = props;

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [props.messages, props.agents]);

  const handleSend = useCallback((msg: ChatMessageRequest) => {
    onSend(msg);
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [onSend]);

  const handleWrite = useCallback((msg: ChatMessageRequest) => {
    onWrite(msg);
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [onWrite]);

  const ctxCache = useMemo(() => new MiddlewareContextFactory({
    send: handleSend,
    write: handleWrite,
    openUrl: onOpenUrl
  }), [handleSend, handleWrite, onOpenUrl]);

  const date = new Date();
  const time = date.getHours() + ':' + date.getMinutes();

  /**
   * Render the message based on widget state or message type. offline and pre chat are pseudo messages.
   *
   * @param msg
   * @param addClass
   */
  function renderByType(msg: ChatDetail, sibling: boolean) {
    let user: ChatUserInfo;

    // visitor is this widget
    if (msg.user?.nick?.startsWith("visitor:")) {
      user = msg.user;
    } else {
      user = props.agents[msg.user.nick]?.user || props.agent;

      // Still nothing?
      if (!user) {
        log(`Could not get a user from agents list with nick: "${msg.user.nick}"`);
        user = msg.user;
      }
    }

    switch (msg.type) {
      case "chat.file":
      case "chat.msg":
        return (
          <ChatMessage
            key={`cm-${msg.type}-${msg.timestamp}`}
            message={msg as ChatMsgDetail}
            sibling={sibling}
            agent={user}
            messageMiddleware={props.messageMiddleware}
            middlewareContext={ctxCache.resolve(user)}
          />
        );
      case "chat.failureMsg":
        return (
          <FailureMessage
            key={`fm-${msg.type}-${msg.timestamp}`}
            agents={props.agents}
            time={time}
            {...msg.failureMsg}
          />
        )
      case "chat.memberjoin":
      case "chat.memberleave":
      case "chat.rating":
      case "chat.typing":
        return (
          <SystemMessage
            key={`sm-${msg.type}-${msg.timestamp}`}
            time={time}
            message={msg}
          />
        );
      case "chat.request.rating":
        return (
          <ChatRatingContainer
            key={`cr-${msg.type}-${msg.timestamp}`}
            agent={user}
            hasRating={props.hasRating}
            time={time}
            shouldDisplay={msg.timestamp === props.lastRatingRequestTimestamp}
          />
        );
      case "chat.offline":
        return <OfflineFormContainer key={`offline-${msg.timestamp}`} time={time} />;
      case "chat.prechat":
        return <PrechatFormContainer key="prechat" time={time} />;
      default:
        return (
          <>
            <span className="message-sr-only">{"at " + time + " system message"}</span>
            <div key={`uhm-${+new Date()}`}>Unhandled message: {JSON.stringify(msg)}</div>
          </>
        );
    }
  }

  /**
   * Render all messages
   *
   */
  function renderAll() {
    let lastMsgIndex = -1;
    for (let i = props.messages.length - 1; i > 0; i--) {
      if (props.messages[i].type === "chat.msg") {
        lastMsgIndex = i;
        break;
      }
    }

    return props.messages.map((message: ChatDetail, index: number) => {
      // const next = props.messages[index + 1];

      // let sibling = false;

      // const currentNick = message.nick;
      // const prevNick = next && next.nick;

      // Same agent says more
      // if (next && next.type === message.type && currentNick && currentNick === prevNick) {
      //   sibling = true;
      // }

      // Message types that will show time 

      const tsTypes = [
        "chat.file",
        "chat.msg",
        // "chat.failureMsg",
        "chat.memberjoin",
        "chat.memberleave",
        "chat.rating",
        // "chat.typing",
        "chat.request.rating",
        "chat.offline",
        "chat.prechat"
      ];

      // show first and las TS and after at least 5 minutes gap. Don't show "router user"

      let showTs = false;

      if (tsTypes.includes(message.type) && !message.user?.nick?.endsWith(ROUTER_USER)) {
        if (index === 0 || index === props.messages.length - 1 || index === lastMsgIndex) {
          showTs = true;
        } else {
          const previous = props.messages[index - 1];
          if (message.timestamp && previous.timestamp) {
            showTs = (message.timestamp - previous.timestamp) > 300 * 1000; // 5 minutes
          }
        }
      }

      const ts = showTs ? new Date(message.timestamp).toLocaleString() : undefined;

      return (
        <>
          {showTs && (
            <div className="ts-msg-container">
              <span className="ts-msg">{ts}</span>
            </div>
          )}
          {renderByType(message, false)}
        </>
      );
    });
  }

  const { agents, queuePosition } = props;

  return (
    <div className="message-list-container">
      <div className="message-list-container__msgs">
        {renderAll()}
        <div ref={messagesEndRef} style={{ float: "left", clear: "both" }} />
        <QueuePosition position={queuePosition} />
        <TypingStatus agents={agents} textTypingStatusEnabled={props.textTypingStatusEnabled} />
      </div>
      <ChatChipsContainer />
    </div>
  );
}

export default MessageList;
